
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import "./CashVouchers.css";

const API =
    process.env.REACT_APP_API_URL || "/api";

const PAYMENT_METHODS = [
    "Cash", "UPI", "Bank Transfer", "NEFT",
    "RTGS", "Cheque", "Other"
];

const createEmptyForm = () => ({
    voucherType: "RECEIPT",
    voucherDate: new Date().toLocaleDateString("en-CA"),
    partyName: "",
    billId: "",
    amount: "",
    amountInWords: "",
    paymentMethod: "Cash",
    referenceNo: "",
    bankName: "",
    reason: ""
});

function money(value) {
    return Number(value || 0).toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}


function amountToWords(value) {
    if (value === "" || value === null || value === undefined) {
        return "";
    }

    const amount = Number(value);

    if (!Number.isFinite(amount) || amount < 0) {
        return "";
    }

    const totalPaise = Math.round(amount * 100);
    const rupees = Math.floor(totalPaise / 100);
    const paise = totalPaise % 100;

    const ones = [
        "Zero", "One", "Two", "Three", "Four", "Five",
        "Six", "Seven", "Eight", "Nine", "Ten",
        "Eleven", "Twelve", "Thirteen", "Fourteen",
        "Fifteen", "Sixteen", "Seventeen", "Eighteen",
        "Nineteen"
    ];

    const tens = [
        "", "", "Twenty", "Thirty", "Forty", "Fifty",
        "Sixty", "Seventy", "Eighty", "Ninety"
    ];

    function convert(number) {
        if (number < 20) {
            return ones[number];
        }

        if (number < 100) {
            return tens[Math.floor(number / 10)] +
                (number % 10 ? " " + ones[number % 10] : "");
        }

        if (number < 1000) {
            return ones[Math.floor(number / 100)] + " Hundred" +
                (number % 100 ? " " + convert(number % 100) : "");
        }

        const groups = [
            [10000000, "Crore"],
            [100000, "Lakh"],
            [1000, "Thousand"]
        ];

        for (const [divisor, label] of groups) {
            if (number >= divisor) {
                return convert(Math.floor(number / divisor)) +
                    " " + label +
                    (number % divisor
                        ? " " + convert(number % divisor)
                        : "");
            }
        }

        return "";
    }

    let result = convert(rupees) + " Rupees";

    if (paise > 0) {
        result += " and " + convert(paise) + " Paise";
    }

    return result + " Only";
}

function dateOnly(value) {
    if (!value) return "";
    if (value instanceof Date) {
        return value.toISOString().slice(0, 10);
    }
    return String(value).slice(0, 10);
}

function escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (char) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;"
    })[char]);
}

export default function CashVouchers() {
    const token = localStorage.getItem("erp_token");

    const [form, setForm] = useState(() => createEmptyForm());
    const [bills, setBills] = useState([]);
    const [vouchers, setVouchers] = useState([]);

    const [billSearch, setBillSearch] = useState("");
    const [showBillResults, setShowBillResults] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [voiceError, setVoiceError] = useState("");
    const [fieldListening, setFieldListening] = useState("");
    const [fieldVoiceError, setFieldVoiceError] = useState("");
    const fieldRecognitionRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [filter, setFilter] = useState("ALL");
    const [search, setSearch] = useState("");

    const recognitionRef = useRef(null);

    const headers = {
        Authorization: `Bearer ${token}`
    };

    // ---------------------------------------------
    // LOAD DATA
    // ---------------------------------------------

    const loadData = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const [voucherResponse, billResponse] =
                await Promise.all([
                    axios.get(`${API}/cash-vouchers`, { headers }),
                    axios.get(`${API}/customer-charges/bills`, { headers })
                ]);

            setVouchers(voucherResponse.data.data || []);
            setBills(billResponse.data.data || []);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                "Unable to load voucher data. Check your backend and login."
            );
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.onresult = null;
                recognitionRef.current.onerror = null;
                recognitionRef.current.onend = null;

                try {
                    recognitionRef.current.abort();
                } catch (_) {
                    // Recognition may already have stopped.
                }
            }

            if (fieldRecognitionRef.current) {
                fieldRecognitionRef.current.onstart = null;
                fieldRecognitionRef.current.onresult = null;
                fieldRecognitionRef.current.onerror = null;
                fieldRecognitionRef.current.onend = null;

                try {
                    fieldRecognitionRef.current.abort();
                } catch (_) {
                    // Recognition may already have stopped.
                }
                fieldRecognitionRef.current = null;
            }
        };
    }, []);

    // ---------------------------------------------
    // INDIVIDUAL FORM FIELD VOICE INPUT
    // ---------------------------------------------

    const startFieldVoiceInput = (fieldName, language = "en-IN") => {
        const SpeechRecognition =
            window.SpeechRecognition ||
            window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            setFieldVoiceError(
                "Voice input is unavailable in this browser. Please use Google Chrome."
            );
            return;
        }

        if (fieldRecognitionRef.current) {
            if (fieldListening === fieldName) {
                try {
                    fieldRecognitionRef.current.stop();
                } catch (_) {
                    // Recognition may already have stopped.
                }
            } else {
                setFieldVoiceError(
                    "Another field is listening. Please wait until it finishes."
                );
            }
            return;
        }

        setFieldVoiceError("");
        setVoiceError("");

        try {
            const recognition = new SpeechRecognition();
            fieldRecognitionRef.current = recognition;
            recognition.lang = language;
            recognition.continuous = false;
            recognition.interimResults = false;
            recognition.maxAlternatives = 1;

            recognition.onstart = () => {
                setFieldListening(fieldName);
            };

            recognition.onresult = (event) => {
                const transcript =
                    event.results?.[0]?.[0]?.transcript?.trim() || "";

                if (!transcript) {
                    setFieldVoiceError("No speech was detected. Please try again.");
                    return;
                }

                let value = transcript;

                if (fieldName === "amount") {
                    // Financial amounts must become a valid numeric value.
                    // Do not guess if speech recognition returns number words.
                    const numeric = transcript
                        .replace(/,/g, "")
                        .replace(/[₹]/g, "")
                        .replace(/\b(?:INR|rupees?|rupee)\b/gi, "")
                        .trim();

                    if (!/^\d+(\.\d{1,2})?$/.test(numeric)) {
                        setFieldVoiceError(
                            'Amount not recognized as a number. Please say the amount clearly, for example "1500" or "1500 point 50", and enter it manually if needed.'
                        );
                        return;
                    }

                    value = numeric;
                }

                setForm((previous) => ({
                    ...previous,
                    [fieldName]: value,
                    ...(fieldName === "amount"
                        ? { amountInWords: amountToWords(value) }
                        : {})
                }));

                setError("");
                setSuccess("");
                setFieldVoiceError("");
            };

            recognition.onerror = (event) => {
                if (event.error === "aborted") return;

                const messages = {
                    "not-allowed": "Microphone permission denied. Allow microphone access in your browser.",
                    "service-not-allowed": "Speech recognition is blocked by the browser.",
                    "no-speech": "No speech detected. Please try again.",
                    "audio-capture": "No microphone was found. Check your microphone connection.",
                    "network": "Speech recognition network error. Check your internet connection."
                };

                setFieldVoiceError(
                    messages[event.error] ||
                    `Voice input failed (${event.error || "unknown error"}). Please try again or type the value.`
                );
            };

            recognition.onend = () => {
                if (fieldRecognitionRef.current === recognition) {
                    fieldRecognitionRef.current = null;
                }
                setFieldListening("");
            };

            recognition.start();
        } catch (error) {
            fieldRecognitionRef.current = null;
            setFieldListening("");
            setFieldVoiceError(
                "Could not start voice input. Check microphone permission and try again."
            );
        }
    };

    // ---------------------------------------------
    // SELECTED BILL
    // ---------------------------------------------

    const selectedBill = bills.find(
        (bill) => String(bill.id) === String(form.billId)
    );

    const amount = Number(form.amount || 0);

    // ---------------------------------------------
    // CUSTOMER BILL SEARCH
    // ---------------------------------------------

    const filteredBills = bills.filter((bill) => {
        const searchTerms = billSearch
            .trim()
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean);

        const searchableDetails = [
            bill.customer_name,
            bill.bill_no,
            bill.grn_no
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        const hasBalance = Number(bill.balance_due || 0) > 0;
        const matches = searchTerms.every((term) =>
            searchableDetails.includes(term)
        );

        return hasBalance && matches;
    });

    const selectBill = (bill) => {
        setForm((previous) => ({
            ...previous,
            billId: String(bill.id),
            partyName: bill.customer_name || ""
        }));

        setBillSearch(
            `${bill.customer_name || "Customer"} — ${bill.bill_no || ""}`
        );

        setShowBillResults(false);
        setVoiceError("");
        setError("");
        setSuccess("");
    };

    const clearSelectedBill = () => {
        setForm((previous) => ({
            ...previous,
            billId: "",
            partyName: ""
        }));

        setBillSearch("");
        setShowBillResults(false);
        setVoiceError("");
    };

    // ---------------------------------------------
    // MARATHI MICROPHONE SEARCH
    // ---------------------------------------------

    const startMarathiVoiceSearch = () => {
        const SpeechRecognition =
            window.SpeechRecognition ||
            window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            setVoiceError(
                "तुमच्या ब्राउझरमध्ये व्हॉइस सर्च उपलब्ध नाही. कृपया Google Chrome वापरा."
            );
            return;
        }

        if (isListening) {
            try {
                recognitionRef.current?.stop();
            } catch (_) {
                // Recognition may already have stopped.
            }
            return;
        }

        setVoiceError("");
        setError("");
        setSuccess("");

        try {
            const recognition = new SpeechRecognition();

            recognition.lang = "mr-IN";
            recognition.continuous = false;
            recognition.interimResults = false;
            recognition.maxAlternatives = 3;

            recognitionRef.current = recognition;

            recognition.onstart = () => {
                setIsListening(true);
                setShowBillResults(true);
            };

            recognition.onresult = (event) => {
                const spokenText =
                    event.results?.[0]?.[0]?.transcript?.trim() || "";

                if (!spokenText) {
                    setVoiceError(
                        "आवाज ओळखता आला नाही. कृपया पुन्हा बोला."
                    );
                    return;
                }

                // Search using the recognized Marathi words.
                setBillSearch(spokenText);
                setShowBillResults(true);

                // Require the user to select the matching bill.
                setForm((previous) => ({
                    ...previous,
                    billId: "",
                    partyName: ""
                }));

                setVoiceError("");
            };

            recognition.onerror = (event) => {
                if (event.error === "not-allowed") {
                    setVoiceError(
                        "मायक्रोफोनची परवानगी द्या आणि पुन्हा प्रयत्न करा."
                    );
                } else if (event.error === "no-speech") {
                    setVoiceError(
                        "आवाज ऐकू आला नाही. कृपया ग्राहकाचे नाव पुन्हा सांगा."
                    );
                } else if (event.error === "audio-capture") {
                    setVoiceError(
                        "मायक्रोफोन सापडला नाही. तुमचा मायक्रोफोन तपासा."
                    );
                } else if (event.error !== "aborted") {
                    setVoiceError(
                        "व्हॉइस सर्च अयशस्वी. कृपया पुन्हा प्रयत्न करा."
                    );
                }
            };

            recognition.onend = () => {
                setIsListening(false);
                recognitionRef.current = null;
            };

            recognition.start();
        } catch (err) {
            setIsListening(false);
            setVoiceError(
                "मायक्रोफोन सुरू करता आला नाही. कृपया पुन्हा प्रयत्न करा."
            );
        }
    };

    // ---------------------------------------------
    // FORM INPUTS
    // ---------------------------------------------

    const update = (event) => {
        const { name, value } = event.target;

        setForm((previous) => ({
            ...previous,
            [name]: value,
            ...(name === "amount"
                ? { amountInWords: amountToWords(value) }
                : {})
        }));

        setError("");
        setSuccess("");
    };

    const changeVoucherType = (type) => {
        if (recognitionRef.current) {
            try {
                recognitionRef.current.abort();
            } catch (_) {
                // Ignore an already stopped recognition session.
            }
        }

        setIsListening(false);
        setVoiceError("");
        setBillSearch("");
        setShowBillResults(false);
        setError("");
        setSuccess("");

        setForm((previous) => ({
            ...previous,
            voucherType: type,
            partyName: "",
            billId: ""
        }));
    };

    const resetForm = () => {
        if (recognitionRef.current) {
            try {
                recognitionRef.current.abort();
            } catch (_) {
                // Ignore an already stopped recognition session.
            }
        }

        setIsListening(false);
        setVoiceError("");
        setBillSearch("");
        setShowBillResults(false);
        setError("");
        setSuccess("");
        setForm(createEmptyForm());
    };

    // ---------------------------------------------
    // SAVE VOUCHER
    // ---------------------------------------------

    const submit = async (event) => {
        event.preventDefault();
        const shouldPrint =
            event.nativeEvent.submitter?.value === "save-and-print";

        setError("");
        setSuccess("");

        if (!String(form.partyName || "").trim()) {
            setError("Please enter the customer or party name.");
            return;
        }

        if (form.voucherType === "RECEIPT" && !form.billId) {
            setError(
                "ग्राहकाचे बिल शोधून यादीतून निवडा."
            );
            return;
        }

        if (
            !Number.isFinite(amount) ||
            amount <= 0 ||
            Math.round(amount * 100) !== amount * 100
        ) {
            setError(
                "Enter a valid amount with up to two decimal places."
            );
            return;
        }

        if (
            form.voucherType === "RECEIPT" &&
            (!selectedBill ||
                amount > Number(selectedBill.balance_due || 0) + 0.001)
        ) {
            setError(
                "Amount exceeds the selected bill's outstanding balance."
            );
            return;
        }

        let printWindow = null;
        if (shouldPrint) {
            printWindow = window.open(
                "",
                "_blank",
                "width=900,height=750"
            );

            if (!printWindow) {
                setError("Allow pop-ups in your browser to print vouchers.");
                return;
            }
        }

        setSaving(true);

        try {
            const payload = {
                ...form,
                partyName: form.partyName.trim(),
                billId:
                    form.voucherType === "RECEIPT"
                        ? Number(form.billId)
                        : null,
                amount
            };

            const response = await axios.post(
                `${API}/cash-vouchers`,
                payload,
                { headers }
            );

            setSuccess(
                `${response.data.message} Voucher No: ${response.data.data.voucherNo}`
            );

            if (shouldPrint) {
                await printVoucher(response.data.data.id, printWindow);
                printWindow = null;
            }

            setForm(createEmptyForm());
            setBillSearch("");
            setShowBillResults(false);
            setVoiceError("");

            await loadData();
        } catch (err) {
            if (printWindow && !printWindow.closed) {
                printWindow.close();
            }

            setError(
                err.response?.data?.message ||
                "Unable to save voucher."
            );
        } finally {
            setSaving(false);
        }
    };

    // ---------------------------------------------
    // VOUCHER HISTORY
    // ---------------------------------------------

    const visibleVouchers = vouchers.filter((voucher) => {
        const matchesType =
            filter === "ALL" ||
            voucher.voucher_type === filter;

        const query = search.trim().toLowerCase();

        const values = [
            voucher.voucher_no,
            voucher.party_name,
            voucher.reference_no
        ];

        const matchesSearch =
            !query ||
            values.some((value) =>
                String(value || "").toLowerCase().includes(query)
            );

        return matchesType && matchesSearch;
    });

    // ---------------------------------------------
    // PRINT VOUCHER
    // ---------------------------------------------

    const printVoucher = async (id, targetWindow = null) => {
        let printWindow = targetWindow;

        try {
            if (!printWindow) {
                printWindow = window.open(
                    "",
                    "_blank",
                    "width=900,height=750"
                );
            }

            if (!printWindow) {
                setError("Allow pop-ups in your browser to print vouchers.");
                return;
            }

            const response = await axios.get(
                `${API}/cash-vouchers/${id}`,
                { headers }
            );

            const voucher = response.data.data;

            const title =
                voucher.voucher_type === "RECEIPT"
                    ? "CASH RECEIPT VOUCHER"
                    : "CASH PAYMENT VOUCHER";

            printWindow.document.write(`
                <!doctype html>
                <html>
                <head>
                    <meta charset="utf-8" />
                    <title>${escapeHtml(voucher.voucher_no)}</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            padding: 32px;
                            color: #222;
                        }

                        .paper {
                            max-width: 760px;
                            margin: auto;
                            border: 1px solid #333;
                            padding: 28px;
                        }

                        h1, h2, p {
                            text-align: center;
                            margin: 5px 0;
                        }

                        h1 { font-size: 22px; }
                        h2 { font-size: 18px; margin: 18px 0; }

                        .meta {
                            display: flex;
                            justify-content: space-between;
                            gap: 15px;
                            margin: 24px 0;
                        }

                        .line {
                            border-bottom: 1px solid #888;
                            padding: 12px 0;
                            overflow-wrap: anywhere;
                        }

                        .amount {
                            text-align: right;
                            font-size: 19px;
                            font-weight: bold;
                            margin-top: 20px;
                        }

                        .signatures {
                            display: flex;
                            justify-content: space-between;
                            gap: 18px;
                            margin-top: 75px;
                        }

                        .signatures span {
                            border-top: 1px solid #333;
                            padding-top: 8px;
                            width: 33%;
                            text-align: center;
                            font-size: 12px;
                        }

                        @media print {
                            body { padding: 0; }
                            .paper { max-width: none; }
                        }
                    </style>
                </head>
                <body>
                    <div class="paper">
                        <h1>Bhimashankar Agro Coldchain &amp; Processing Producer Co. Ltd.</h1>
                        <p>Mob. 9822974495 / 9921366271 / 9921252022</p>
                        <p>bhimashankaragrocoldchain@gmail.com</p>
                        
                        <h2>${title}</h2>

                        <div class="meta">
                            <span>Voucher No: <b>${escapeHtml(voucher.voucher_no)}</b></span>
                            <span>Date: <b>${escapeHtml(dateOnly(voucher.voucher_date))}</b></span>
                        </div>

                        <div class="line">Name / Party: <b>${escapeHtml(voucher.party_name)}</b></div>
                        <div class="line">Customer Bill: <b>${escapeHtml(voucher.customer_bill_no || "—")}</b></div>
                        <div class="line">Amount in words: <b>${escapeHtml(voucher.amount_in_words || "—")}</b></div>
                        <div class="line">Payment method: <b>${escapeHtml(voucher.payment_method)}</b></div>
                        <div class="line">Reference / Cheque No: <b>${escapeHtml(voucher.reference_no || "—")}</b></div>
                        <div class="line">Bank: <b>${escapeHtml(voucher.bank_name || "—")}</b></div>
                        <div class="line">Reason: <b>${escapeHtml(voucher.reason || "—")}</b></div>

                        <div class="amount">Amount: ${money(voucher.amount)}</div>

                        <div class="signatures">
                            <span>Prepared By</span>
                            <span>Received / Paid By</span>
                            <span>Authorized Signature</span>
                        </div>
                    </div>

                    <script>
                        window.onload = function() {
                            window.print();
                        };
                    <\/script>
                </body>
                </html>
            `);

            printWindow.document.close();
        } catch (err) {
            if (printWindow && !printWindow.closed) {
                printWindow.close();
            }

            setError(
                err.response?.data?.message ||
                "Unable to load voucher for printing."
            );
        }
    };

    // ---------------------------------------------
    // PAGE
    // ---------------------------------------------

    return (
        <main className="cash-vouchers-page">
            <header className="cv-header">
                <div>
                    <span className="cv-eyebrow">
                        FINANCE MANAGEMENT
                    </span>
                    <h1>Cash Vouchers</h1>
                    <p>Manage customer receipts and cash payments.</p>
                </div>

                <button
                    className="cv-refresh"
                    type="button"
                    onClick={loadData}
                    disabled={loading}
                >
                    {loading ? "Refreshing..." : "Refresh Data"}
                </button>
            </header>

            {error && (
                <div className="cv-alert cv-error">{error}</div>
            )}

            {success && (
                <div className="cv-alert cv-success">{success}</div>
            )}

            {/* CREATE VOUCHER */}

            <section className="cv-panel">
                <div className="cv-panel-heading">
                    <div>
                        <h2>Create Voucher</h2>
                        <p>Enter the transaction details below.</p>
                    </div>

                    <div className="cv-type-switch">
                        <button
                            type="button"
                            className={
                                form.voucherType === "RECEIPT"
                                    ? "active receipt"
                                    : ""
                            }
                            onClick={() => changeVoucherType("RECEIPT")}
                        >
                            Cash Receipt
                        </button>

                    </div>
                </div>

                <form onSubmit={submit}>
                    <div className="cv-form-grid">
                        <label>
                            Voucher Date *
                            <input
                                type="date"
                                name="voucherDate"
                                value={form.voucherDate}
                                onChange={update}
                                required
                            />
                        </label>

                        {/* SEARCH + MARATHI MICROPHONE */}

                        {form.voucherType === "RECEIPT" && (
                            <label className="cv-bill-search-field">
                                Customer Bill *

                                <div className="cv-search-container">
                                    <div className="cv-bill-search-input">
                                        <input
                                            type="text"
                                            value={billSearch}
                                            placeholder="ग्राहकाचे नाव किंवा बिल नंबर शोधा..."
                                            autoComplete="off"
                                            required={!form.billId}
                                            onChange={(event) => {
                                                setBillSearch(event.target.value);
                                                setShowBillResults(true);

                                                setForm((previous) => ({
                                                    ...previous,
                                                    billId: "",
                                                    partyName: ""
                                                }));

                                                setError("");
                                                setSuccess("");
                                                setVoiceError("");
                                            }}
                                            onFocus={() => {
                                                setShowBillResults(true);
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key === "Escape") {
                                                    setShowBillResults(false);
                                                }
                                            }}
                                        />

                                        <button
                                            type="button"
                                            className={`cv-mic-button ${
                                                isListening ? "listening" : ""
                                            }`}
                                            onClick={startMarathiVoiceSearch}
                                            title="मराठीत बोलून ग्राहक शोधा"
                                            aria-label="मराठी व्हॉइस सर्च"
                                        >
                                            {isListening ? "🔴" : "🎙️"}
                                        </button>

                                        {form.billId && (
                                            <button
                                                type="button"
                                                className="cv-clear-bill"
                                                aria-label="Clear selected bill"
                                                onClick={clearSelectedBill}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>

                                    {isListening && (
                                        <small className="cv-voice-status">
                                            🎙️ ऐकत आहे... ग्राहकाचे नाव मराठीत सांगा.
                                        </small>
                                    )}

                                    {voiceError && (
                                        <small className="cv-voice-error">
                                            {voiceError}
                                        </small>
                                    )}

                                    {showBillResults &&
                                        billSearch.trim() &&
                                        !form.billId && (
                                            <div className="cv-search-results">
                                                {filteredBills.length > 0 ? (
                                                    filteredBills.map((bill) => (
                                                        <button
                                                            type="button"
                                                            key={bill.id}
                                                            className="cv-search-result"
                                                            onClick={() => selectBill(bill)}
                                                        >
                                                            <span>
                                                                <strong>
                                                                    {bill.customer_name}
                                                                </strong>
                                                                <small>
                                                                    Bill: {bill.bill_no}
                                                                </small>
                                                                {bill.grn_no && (
                                                                    <small>
                                                                        GRN: {bill.grn_no}
                                                                    </small>
                                                                )}
                                                            </span>

                                                            <span className="cv-search-due">
                                                                Due: {money(bill.balance_due)}
                                                            </span>
                                                        </button>
                                                    ))
                                                ) : (
                                                    <div className="cv-search-empty">
                                                        कोणतेही बाकी असलेले बिल सापडले नाही.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                </div>

                                {form.billId && (
                                    <small className="cv-selected-bill">
                                        निवडलेले बिल: {selectedBill?.bill_no || ""}
                                    </small>
                                )}
                            </label>
                        )}

                        <label>
                            {form.voucherType === "RECEIPT"
                                ? "Customer Name *"
                                : "Paid To / Party Name *"}

                            <div className="cv-field-voice">
                                <input
                                    name="partyName"
                                    value={form.partyName}
                                    onChange={update}
                                    placeholder="Enter party name"
                                    readOnly={
                                        form.voucherType === "RECEIPT" &&
                                        Boolean(form.billId)
                                    }
                                    required
                                />
                                {!(form.voucherType === "RECEIPT" && Boolean(form.billId)) && (
                                    <button
                                        type="button"
                                        className={`cv-field-mic ${fieldListening === "partyName" ? "listening" : ""}`}
                                        onClick={() => startFieldVoiceInput("partyName", "mr-IN")}
                                        disabled={Boolean(fieldListening && fieldListening !== "partyName")}
                                        aria-label="Speak party name"
                                        title="Speak party name in Marathi or English"
                                    >
                                        {fieldListening === "partyName" ? "🔴" : "🎙️"}
                                    </button>
                                )}
                            </div>
                        </label>

                        <label>
                            Amount (₹) *
                            <div className="cv-field-voice">
                                <input
                                    type="number"
                                    name="amount"
                                    min="0.01"
                                    step="0.01"
                                    value={form.amount}
                                    onChange={update}
                                    placeholder="0.00"
                                    required
                                />
                                <button
                                    type="button"
                                    className={`cv-field-mic ${fieldListening === "amount" ? "listening" : ""}`}
                                    onClick={() => startFieldVoiceInput("amount", "en-IN")}
                                    disabled={Boolean(fieldListening && fieldListening !== "amount")}
                                    aria-label="Speak amount"
                                    title="Speak the amount as digits"
                                >
                                    {fieldListening === "amount" ? "🔴" : "🎙️"}
                                </button>
                            </div>
                        </label>

                        <label>
                            Payment Method *
                            <select
                                name="paymentMethod"
                                value={form.paymentMethod}
                                onChange={update}
                                required
                            >
                                {PAYMENT_METHODS.map((method) => (
                                    <option key={method} value={method}>
                                        {method}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label>
                            Reference / Cheque No.
                            <div className="cv-field-voice">
                                <input
                                    name="referenceNo"
                                    value={form.referenceNo}
                                    onChange={update}
                                    placeholder="Transaction reference"
                                />
                                <button
                                    type="button"
                                    className={`cv-field-mic ${fieldListening === "referenceNo" ? "listening" : ""}`}
                                    onClick={() => startFieldVoiceInput("referenceNo", "en-IN")}
                                    disabled={Boolean(fieldListening && fieldListening !== "referenceNo")}
                                    aria-label="Speak reference number"
                                    title="Speak transaction reference"
                                >
                                    {fieldListening === "referenceNo" ? "🔴" : "🎙️"}
                                </button>
                            </div>
                        </label>

                        <label>
                            Bank Name
                            <div className="cv-field-voice">
                                <input
                                    name="bankName"
                                    value={form.bankName}
                                    onChange={update}
                                    placeholder="Enter bank name"
                                />
                                <button
                                    type="button"
                                    className={`cv-field-mic ${fieldListening === "bankName" ? "listening" : ""}`}
                                    onClick={() => startFieldVoiceInput("bankName", "en-IN")}
                                    disabled={Boolean(fieldListening && fieldListening !== "bankName")}
                                    aria-label="Speak bank name"
                                    title="Speak bank name"
                                >
                                    {fieldListening === "bankName" ? "🔴" : "🎙️"}
                                </button>
                            </div>
                        </label>

                        <label>
                            Amount in Words
                            <input
                                name="amountInWords"
                                value={form.amountInWords}
                                readOnly
                                placeholder="Automatically generated from amount"
                            />
                        </label>

                        <label className="cv-full">
                            Reason / Remarks
                            <div className="cv-field-voice">
                                <input
                                    name="reason"
                                    value={form.reason}
                                    onChange={update}
                                    placeholder="Additional reason or remarks"
                                />
                                <button
                                    type="button"
                                    className={`cv-field-mic ${fieldListening === "reason" ? "listening" : ""}`}
                                    onClick={() => startFieldVoiceInput("reason", "mr-IN")}
                                    disabled={Boolean(fieldListening && fieldListening !== "reason")}
                                    aria-label="Speak reason or remarks"
                                    title="Speak reason or remarks in Marathi or English"
                                >
                                    {fieldListening === "reason" ? "🔴" : "🎙️"}
                                </button>
                            </div>
                        </label>

                    </div>

                    {fieldListening && (
                        <div className="cv-voice-status" role="status">
                            🎙️ Listening... Please speak clearly.
                        </div>
                    )}

                    {fieldVoiceError && (
                        <div className="cv-voice-error" role="alert">
                            {fieldVoiceError}
                        </div>
                    )}

                    {selectedBill && form.voucherType === "RECEIPT" && (
                        <div className="cv-bill-summary">
                            <div>
                                <small>Bill Total</small>
                                <strong>{money(selectedBill.total_amount)}</strong>
                            </div>
                            <div>
                                <small>Already Paid</small>
                                <strong>{money(selectedBill.amount_paid)}</strong>
                            </div>
                            <div>
                                <small>Balance Due</small>
                                <strong>{money(selectedBill.balance_due)}</strong>
                            </div>
                        </div>
                    )}

                    <div className="cv-form-actions">
                        <button
                            type="button"
                            className="cv-secondary"
                            onClick={resetForm}
                        >
                            Clear Form
                        </button>

                        <button
                            className="cv-save-print"
                            type="submit"
                            name="submitAction"
                            value="save-and-print"
                            disabled={saving}
                        >
                            {saving
                                ? "Saving..."
                                : `Save & Print ${
                                    form.voucherType === "RECEIPT"
                                        ? "Receipt"
                                        : "Payment"
                                }`}
                        </button>

                        <button
                            className="cv-submit"
                            type="submit"
                            name="submitAction"
                            value="save"
                            disabled={saving}
                        >
                            {saving
                                ? "Saving..."
                                : `Save ${
                                    form.voucherType === "RECEIPT"
                                        ? "Receipt"
                                        : "Payment"
                                } Voucher`}
                        </button>
                    </div>
                </form>
            </section>

            {/* VOUCHER HISTORY */}

            <section className="cv-panel">
                <div className="cv-panel-heading">
                    <div>
                        <h2>Voucher History</h2>
                        <p>{visibleVouchers.length} voucher(s) found.</p>
                    </div>

                    <button
                        type="button"
                        className="cv-secondary"
                        onClick={loadData}
                        disabled={loading}
                    >
                        Reload
                    </button>
                </div>

                <div className="cv-toolbar">
                    <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search voucher, party or reference..."
                    />

                    <select
                        value={filter}
                        onChange={(event) => setFilter(event.target.value)}
                    >
                        <option value="ALL">All Vouchers</option>
                        <option value="RECEIPT">Cash Receipts</option>
                        <option value="PAYMENT">Cash Payments</option>
                    </select>
                </div>

                <div className="cv-table-wrap">
                    <table className="cv-table">
                        <thead>
                            <tr>
                                <th>Voucher No.</th>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Party</th>
                                <th>Method</th>
                                <th>Amount</th>
                                <th>Print</th>
                            </tr>
                        </thead>

                        <tbody>
                            {visibleVouchers.map((voucher) => (
                                <tr key={voucher.id}>
                                    <td>
                                        <strong>{voucher.voucher_no}</strong>
                                    </td>
                                    <td>{dateOnly(voucher.voucher_date)}</td>
                                    <td>
                                        <span
                                            className={`cv-badge ${String(
                                                voucher.voucher_type
                                            ).toLowerCase()}`}
                                        >
                                            {voucher.voucher_type}
                                        </span>
                                    </td>
                                    <td>{voucher.party_name}</td>
                                    <td>{voucher.payment_method}</td>
                                    <td className="cv-amount">
                                        {money(voucher.amount)}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="cv-print"
                                            onClick={() => printVoucher(voucher.id)}
                                        >
                                            Print
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {!visibleVouchers.length && (
                                <tr>
                                    <td colSpan="8" className="cv-empty">
                                        {loading
                                            ? "Loading vouchers..."
                                            : "No vouchers found."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </main>
    );
}
